import * as React from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import {
    Box,
    TextInput,
    Switch,
    Group,
    Button,
    Textarea,
    NumberInput, MultiSelect
} from '@mantine/core'
import { useForm, isNotEmpty } from "@mantine/form";
import axios from 'axios';
import { CLIENT_ROUTE, API_ROUTE, CLIENT_TITLE } from "./constants";
import { showMessage } from "../../lib/notification";
import type { IResponse } from "../../types/api";
import type { IPlace } from "./types";
import Page from "../../components/layout/Page";
import { ISongGroup } from "../song-group/types";
import MapComponent from "../../components/map/Map";
import { IconMapPin } from "@tabler/icons-react";
import SearchableSelect from "../../components/searchable-select/SearchableSelect";
import FileUpload from "../../components/file-upload";
import { FileWithPath, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { players } from "./constants";
import { assignFiles } from "../../utils/form";
import MultiFileUpload from "../../components/multi-file-upload";
import { transformImageValues } from "./helper";

interface FormValues extends Pick<IPlace,
    'title' |
    'titleEn' |
    'country' |
    'city' |
    'district' |
    'address' |
    'lastInstalledGroupNumberLabel' |
    'fbAddress' |
    'phoneNumber' |
    'playerCount' |
    'players' |
    'isFeatured' |
    'featuredOrder' |
    'isActive'
    > {
    image: File | null;
    location: any;
    covers: (string | FileWithPath)[];
}

interface IParams extends Omit<FormValues, 'image'> {
    image?: string;
    covers: string[];
}

interface IGroupNumberItem extends Pick<ISongGroup, '_id' | 'groupNumber' | 'groupTitle' | 'isActive' | 'isDeleted'> {
}

const EditPlace = () => {

    const { state: id = null } = useLocation();

    const navigate = useNavigate();

    const [ showMap, setShowMap ] = React.useState(false);

    const form = useForm<FormValues>({
        initialValues: {
            address: "",
            city: "",
            country: "",
            covers: [],
            district: "",
            fbAddress: "",
            featuredOrder: 0,
            image: null,
            isFeatured: false,
            lastInstalledGroupNumberLabel: "",
            phoneNumber: "",
            playerCount: "",
            players: [],
            title: "",
            titleEn: "",
            location: undefined,
            isActive: false,
        },

        validate: {
            title: isNotEmpty('Байгууллагын нэр оруулна уу'),
            country: isNotEmpty('Улс оруулна уу'),
            city: isNotEmpty('Хот оруулна уу'),
            district: isNotEmpty('Дүүрэг оруулна уу'),
            address: isNotEmpty('Байгууллагын нэр оруулна уу'),
            lastInstalledGroupNumberLabel: isNotEmpty('Урын сан оруулна уу'),
            playerCount: isNotEmpty('Тоглуулагчийн тоо оруулна уу'),
            players: isNotEmpty('Тоглуулагч оруулна уу'),
        },
    });

    const groupNumberParser = ((groupNumber: IGroupNumberItem) => {
        return {
            value: groupNumber.groupNumber.toString(),
            label: `( ${groupNumber.groupNumber} ) - ${groupNumber.groupTitle}`,
        }
    })

    React.useEffect(() => {
        const fetchData = async () => {
            const { data } = await axios.get<IResponse<IPlace>>(`${API_ROUTE}/${id}`);
            // @ts-ignore
            form.setValues(data.result);
            form.resetDirty();
        }
        void fetchData();
    }, []);

    const handleSubmit = async (values: typeof form.values) => {

        const files = await assignFiles({
            image: values.image
        }, 'place');

        const covers = await transformImageValues(values.covers);

        const params: IParams = {
            ...values,
            ...files,
            covers
        }
        if (params.location) {
            params.location = {
                lat: params.location.lat,
                lon: params.location.lng ?? params.location.lon
            }
        }
        if (!values.isFeatured) {
            params.featuredOrder = 100;
        }
        const { data } = await axios.post<IResponse<any>>(`${API_ROUTE}/${id}`, params);

        if (data.statusCode === 200) {
            navigate(CLIENT_ROUTE);
            showMessage('Амжилттай', 'green');
        } else {
            showMessage('Алдаа гарлаа',);
        }
    }

    return (
        <Page>
            <Page.Header title={`${CLIENT_TITLE} засах`} backRoute={CLIENT_ROUTE}/>
            <Page.Body>
                {
                    showMap && (
                        <MapComponent
                            onClose={() => setShowMap(false)}
                            onSave={(value: any) => {
                                form.setFieldValue('location', value);
                                setShowMap(false);
                            }}
                            value={form.values.location}
                        />
                    )
                }
                <Box sx={{ maxWidth: 500 }} mx="auto">
                    <form>
                        <Button
                            leftIcon={<IconMapPin/>}
                            onClick={() => setShowMap(true)}
                        >
                            Хаяг сонгох
                        </Button>
                        <TextInput
                            withAsterisk
                            label="Байгууллагын нэр"
                            placeholder="Байгууллагын нэр"
                            {...form.getInputProps('title')}
                        />

                        <TextInput
                            label="Байгууллагын нэр Англи"
                            placeholder="Байгууллагын нэр Англи"
                            {...form.getInputProps('titleEn')}
                        />

                        <TextInput
                            withAsterisk
                            label="Улс"
                            placeholder="Улс"
                            {...form.getInputProps('country')}
                        />

                        <TextInput
                            withAsterisk
                            label="Хот"
                            placeholder="Хот"
                            {...form.getInputProps('city')}
                        />

                        <TextInput
                            withAsterisk
                            label="Дүүрэг"
                            placeholder="Дүүрэг"
                            {...form.getInputProps('district')}
                        />

                        <Textarea
                            withAsterisk
                            label="Хаяг"
                            placeholder="Хаяг"
                            {...form.getInputProps('address')}
                        />

                        <SearchableSelect<IGroupNumberItem>
                            label='Урын сан'
                            withAsterisk
                            uri={'/api/song-group/search'}
                            parser={groupNumberParser}
                            onChange={(value) => {
                                if (value) {
                                    form.setFieldValue('lastInstalledGroupNumberLabel', value)
                                }
                            }}
                            value={form.values.lastInstalledGroupNumberLabel}
                        />

                        <FileUpload
                            value={form.values.image}
                            onChange={(value) => {
                                form.setFieldValue('image', value);
                            }}
                            label='Зураг'
                            accept={IMAGE_MIME_TYPE}
                        />

                        <MultiFileUpload
                            accept={IMAGE_MIME_TYPE}
                            value={form.values.covers}
                            label={'Өрөөний зураг'}
                            onChange={(value) => {
                                form.setFieldValue('covers', value)
                            }}
                        />

                        <TextInput
                            label="Facebook хаяг"
                            placeholder="Facebook хаяг"
                            {...form.getInputProps('fbAddress')}
                        />

                        <TextInput
                            label="Утасны дугаар"
                            placeholder="Утасны дугаар"
                            {...form.getInputProps('phoneNumber')}
                        />

                        <NumberInput
                            label="Тоглуулагчийн тоо"
                            placeholder="Тоглуулагчийн тоо"
                            {...form.getInputProps('playerCount')}
                        />

                        <MultiSelect
                            withAsterisk
                            data={players}
                            label={'Тоглуулагчид'}
                            {...form.getInputProps('players')}
                        />

                        <Switch
                            label="Онцолсон эсэх"
                            {...form.getInputProps('isFeatured', { type: 'checkbox' })}
                        />

                        {
                            form.values.isFeatured && (
                                <NumberInput
                                    hideControls
                                    label="Онцолсон дараалал"
                                    placeholder="Онцолсон дараалал"
                                    {...form.getInputProps('featuredOrder')}
                                />
                            )
                        }

                        <Switch
                            label="Идэвхтэй эсэх"
                            {...form.getInputProps('isActive', { type: 'checkbox' })}
                        />
                    </form>
                </Box>
            </Page.Body>
            <Page.Footer>
                <Group position="right" mt="md">
                    <Button color='color-secondary' onClick={() => form.onSubmit(handleSubmit)()}>Хадгалах</Button>
                </Group>
            </Page.Footer>
        </Page>
    );
};

export default EditPlace;